/**
 * /admin/invites
 *
 * Shows all invites (newest first) + a generate-new-invite form.
 * After creating an invite, shows a copy-the-link affordance with
 * the full https URL so you can paste it into a text to the invitee.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, ApiError } from '../../api/client.js';
import './InvitesAdminPage.css';
import { usePageTitle } from '../../hooks/usePageTitle.js';
import PhotosGroupPicker from '../../components/PhotosGroupPicker.jsx';


function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  const diffMs = today - d;
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffDays < 1) return 'today';
  if (diffDays < 2) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusOf(invite) {
  if (invite.used_at) return 'accepted';
  if (invite.revoked_at) return 'revoked';
  if (new Date(invite.expires_at) < new Date()) return 'expired';
  return 'pending';
}


export default function InvitesAdminPage() {
  usePageTitle('Admin · Invites');

  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // generate form state
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('standard');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [photosGroups, setPhotosGroups] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // most recently created invite — surfaced prominently with the copy link
  const [justCreated, setJustCreated] = useState(null);

  const loadInvites = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await api.get('/api/admin/invites');
      setInvites(data);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.detail : 'failed to load invites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  async function handleGenerate(e) {
    e.preventDefault();
    if (!email.trim() || submitting) return;

    setSubmitting(true);
    setFormError('');
    setJustCreated(null);

    try {
      const invite = await api.post('/api/admin/invites', {
        email: email.trim(),
        role,
        expires_in_days: Number(expiresInDays),
        module_config: photosGroups.length > 0 ? { photos: { group_memberships: photosGroups } } : null,
      });
      setJustCreated(invite);
      setEmail('');
      setPhotosGroups([]);
      // role + expires stay the same — usually you invite a batch with the same settings
      loadInvites();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.detail : 'something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRevoke(id) {
    if (!confirm('Revoke this invite? The link will stop working immediately.')) return;
    try {
      await api.del(`/api/admin/invites/${id}`);
      loadInvites();
    } catch (err) {
      alert(err instanceof ApiError ? err.detail : 'failed to revoke');
    }
  }

  const acceptUrlFor = useCallback((token) => {
    return `${window.location.origin}/accept-invite/${token}`;
  }, []);

  const pendingCount = useMemo(
    () => invites.filter((i) => statusOf(i) === 'pending').length,
    [invites]
  );

  return (
    <>
      <div className="admin-page-head">
        <div className="admin-crumbs">
          <a href="/admin">admin</a>
          <span className="sep">/</span>
          invites
        </div>
        <div className="admin-section-head">
          <div>
            <h1 className="admin-section-title">Invites</h1>
            <p className="admin-section-sub">
              hand out keys to the kingdom, one at a time
            </p>
          </div>
        </div>
      </div>

      <div className="admin-content">
        {/* ── generate form ── */}
        <section className="invite-form-card">
          <div className="invite-form-head">
            <div className="invite-form-title">Generate a new invite</div>
            <div className="invite-form-sub">
              enter an email, pick a role, share the link
            </div>
          </div>

          <form onSubmit={handleGenerate} className="invite-form">
            <div className="invite-form-grid">
              <div className="invite-field invite-field-email">
                <label className="invite-label">email</label>
                <input
                  type="email"
                  placeholder="amanda@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                  required
                />
              </div>

              <div className="invite-field">
                <label className="invite-label">role</label>
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="standard">standard</option>
                  <option value="admin">admin</option>
                  <option value="guest">guest</option>
                </select>
              </div>

              <div className="invite-field">
                <label className="invite-label">expires</label>
                <select
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                >
                  <option value="1">1 day</option>
                  <option value="3">3 days</option>
                  <option value="7">7 days</option>
                  <option value="14">14 days</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                </select>
              </div>

              <PhotosGroupPicker value={photosGroups} onChange={setPhotosGroups} />
              <div className="invite-field invite-submit-wrap">
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={submitting || !email.trim()}
                >
                  {submitting ? 'generating…' : '+ generate invite'}
                </button>
              </div>
            </div>

            {formError && (
              <div className="invite-form-error">⚠ {formError}</div>
            )}
          </form>

          {justCreated && (
            <div className="invite-created">
              <div className="invite-created-head">
                <span className="invite-created-icon">✓</span>
                <div>
                  <div className="invite-created-title">
                    invite created for <strong>{justCreated.email}</strong>
                  </div>
                  <div className="invite-created-sub">
                    copy the link below and share it however you want —
                    text, email, carrier pigeon
                  </div>
                </div>
              </div>
              <CopyLink url={acceptUrlFor(justCreated.token)} />
            </div>
          )}
        </section>

        {/* ── list ── */}
        <section className="invite-list-section">
          <div className="invite-list-head">
            <h2 className="invite-list-title">
              All invites
              {!loading && (
                <span className="invite-count">
                  {pendingCount} pending · {invites.length} total
                </span>
              )}
            </h2>
          </div>

          {loading ? (
            <div className="invite-list-empty">loading…</div>
          ) : loadError ? (
            <div className="invite-list-empty error">⚠ {loadError}</div>
          ) : invites.length === 0 ? (
            <div className="invite-list-empty">
              no invites yet. generate one above to get started.
            </div>
          ) : (
            <div className="invite-table">
              <div className="invite-row invite-row-head">
                <div>Email</div>
                <div>Role</div>
                <div>Created</div>
                <div>Expires</div>
                <div>Status</div>
                <div style={{ textAlign: 'right' }}>Actions</div>
              </div>
              {invites.map((inv) => {
                const stat = statusOf(inv);
                return (
                  <div key={inv.id} className="invite-row">
                    <div className="invite-email">{inv.email}</div>
                    <div className="invite-role">{inv.role}</div>
                    <div className="invite-date">{formatDate(inv.created_at)}</div>
                    <div className="invite-date">{formatDate(inv.expires_at)}</div>
                    <div>
                      <span className={`invite-status ${stat}`}>{stat}</span>
                    </div>
                    <div className="invite-actions">
                      {stat === 'pending' && (
                        <>
                          <CopyButton
                            url={acceptUrlFor(inv.token)}
                            label="copy link"
                          />
                          <button
                            className="admin-btn admin-btn-danger-ghost"
                            onClick={() => handleRevoke(inv.id)}
                          >
                            revoke
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </>
  );
}


function CopyLink({ url }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert('Copy failed — select the link manually and copy.');
    }
  }

  return (
    <div className="invite-copy-link">
      <code className="invite-copy-url">{url}</code>
      <button className="admin-btn admin-btn-primary" onClick={copy}>
        {copied ? 'copied ✓' : 'copy link'}
      </button>
    </div>
  );
}


function CopyButton({ url, label }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      alert('Copy failed');
    }
  }
  return (
    <button className="admin-btn admin-btn-ghost" onClick={copy}>
      {copied ? 'copied ✓' : label}
    </button>
  );
}
